// src/Sections/HeroSection/styles.ts
import styled from 'styled-components';
import { MEDIA_BREAKPOINTS, SHORT_HEIGHT } from '@/constants/layout';
import Container from '@/components/StyledComponents/Container';
import CircleImageWithText from '@/components/StyledComponents/CircleImageWithText';

export const VisuallyHidden = styled.div`
  position: absolute !important;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;

export const HeroSectionContainer = styled.section`
  --orbit-pad: 72px;
  --hero-circle-size: clamp(150px, 42vw, 180px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  max-height: 100%;
  overflow-y: visible;
  overflow-x: clip;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    min-height: 100dvh;
    padding-top: calc(env(safe-area-inset-top, 0px) + 15px);
    padding-bottom: calc(
      env(safe-area-inset-bottom, 10px) + var(--orbit-pad) * 0.5
    );

    --rail-gap: clamp(4px, 1vh, 12px);
    row-gap: var(--rail-gap);

    min-height: 0;
  }
`;

const RowBase = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 100%;
  overflow: visible;

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    margin-top: 10px;
  }
`;

export const TopContainer = styled(RowBase)``;

export const CenterContainer = styled(RowBase)`
  flex: 1 1 auto;
  justify-content: center;
  align-self: stretch;
  min-height: 0;
`;

export const BottomContainer = styled(RowBase)`
  margin-top: clamp(10px, 4vh, 40px);
  padding-bottom: 10px;
  position: relative;
  overflow: visible;
  min-height: 220px;

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    display: none;
    min-height: 0;
    margin: 0;
    padding: 0;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    display: flex;
    margin-top: clamp(10px, 2svh, 18px);
    padding-top: 26px;
    padding-bottom: calc(env(safe-area-inset-bottom, 10px) + 22px);
    min-height: calc(var(--hero-circle-size) + 52px);
  }

  @media (max-height: ${SHORT_HEIGHT}) and (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    margin-top: clamp(8px, 1.6svh, 14px);
    padding-top: 18px;
    padding-bottom: calc(env(safe-area-inset-bottom, 8px) + 14px);
    min-height: calc(var(--hero-circle-size) + 34px);
  }
`;

export const Circle = styled(CircleImageWithText)`
  --size: min(60vw, 320px);
  width: var(--size);
  height: var(--size);
  aspect-ratio: 1 / 1;

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    display: none;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    --size: var(--hero-circle-size);
    transform: none;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (max-height: 560px) {
    transform: none;
  }
`;

export const LogoWrapper = styled.div`
  margin-bottom: clamp(3px, 0.7vh, 8px);

  @media (max-height: ${SHORT_HEIGHT}) and (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    margin-bottom: 2px;
  }

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    display: none;
  }
`;

export const DescriptionWrapper = styled(Container)`
  overflow: visible;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (min-height: 800px) {
    margin-bottom: 10px;
  }
`;

export const DescriptionPad = styled.div`
  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (min-height: 800px) {
    padding-top: 15px !important;
  }
`;

export const VideoStyle = styled.div`
  --video-aspect-ratio: 2.164;
  --video-height-cap: clamp(180px, 48svh, 540px);
  width: min(100%, calc(var(--video-height-cap) * var(--video-aspect-ratio)));
  height: auto;
  aspect-ratio: 2160 / 998;
  flex: 0 1 auto;
`;

export const VideoBox = styled(VideoStyle)`
  margin-inline: auto;

  display: flex;
  align-items: center;
  justify-content: center;

  > * {
    width: 100%;
    height: 100%;
  }

  video,
  iframe {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  /* דסקטופ - גדל קצת, לא ענק */
  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    --video-height-cap: clamp(275px, 43svh, 455px);
    width: min(
      1000px,
      78vw,
      calc(var(--video-height-cap) * var(--video-aspect-ratio))
    );
  }

  /* דסקטופ גדול - תקרה שמרנית */
  @media (min-width: ${MEDIA_BREAKPOINTS.largeScreen}) {
    --video-height-cap: clamp(300px, 46svh, 500px);
    width: min(
      1120px,
      72vw,
      calc(var(--video-height-cap) * var(--video-aspect-ratio))
    );
  }

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (max-height: 820px) {
    --video-height-cap: clamp(260px, 40svh, 390px);
  }
`;

export const CirclePlaceholder = styled.div`
  --size: min(60vw, 320px);
  width: var(--size);
  height: var(--size);
  aspect-ratio: 1 / 1;

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    display: none;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    --size: var(--hero-circle-size);
    transform: none;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (max-height: 560px) {
    transform: none;
  }
`;

export const ShortViewportGuard = styled.div`
  overflow: visible;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (max-height: 560px) {
    ${HeroSectionContainer} {
      --hero-circle-size: clamp(108px, 17svh, 140px);
      --rail-gap: clamp(6px, 2vh, 18px);
      padding-bottom: calc(
        env(safe-area-inset-bottom, 10px) + var(--orbit-pad) * 0.5
      );
    }

    ${VideoBox} {
      --video-height-cap: clamp(150px, 40svh, 420px);
    }

    ${Circle},
    ${CirclePlaceholder} {
      --size: clamp(110px, 16vh, 150px);
    }
  }
`;
